import React, { useState, useEffect, useRef, useCallback } from "react";
import L from "leaflet";
import "leaflet.markercluster";
import imgPin from "src/assets/siteManagement/images/pin.svg";
import { useParams } from "react-router-dom";
import { createPropertyPopupHTMLInline } from "../popupUtils";
import BtnRiskIcon from "../../btn-risk-on-site-map/BtnRiskIcon";
import CardTimeForeCast from "../../card-time-forecast-on-site-map/CardTimeForeCast";
import { getRiskColor, getRiskIcon } from "./riskUtils";
import { getRiskOnSiteMapDap } from "src/api/site";
import { riskGroup } from "../../btn-risk-on-site-map/riskGroup";
import moment from "moment";

let map;

function DapGisMapComponent(props) {
  const { setIsLoading } = props;
  const [id, setId] = useState("");
  const [gisMap, setGisMap] = useState(null);
  const divMapRef = useRef(null);
  const [mapPosition] = useState({
    latitude: 13.8583,
    longitude: 100.4688,
    zoom: 10,
    zoomSnap: 0.5,
    maxZoom: 19,
    minZoom: 5,
  });
  const [listMarker, setListMarker] = useState([]);
  const listSiteRef = useRef([]);
  const [markerCluster, setMarkerCluster] = useState(null);
  const [currentLayer, setCurrentLayer] = useState("satellite"); // "satellite" or "street"
  const [currentTimeForecast, setCurrentTimeForecast] = useState(0);
  const [riskData, setRiskData] = useState([]);
  const [currentRisk, setCurrentRisk] = useState(null);
  const [latestTimeForTheData, setLatestTimeForTheData] = useState("");
  const riskMarkersRef = useRef([]);
  const riskCirclesRef = useRef([]);
  const currentTileLayerRef = useRef(null);
  const latLngTopLeftRef = useRef(null);
  const latLngBottomRightRef = useRef(null);
  const isInitFirstFinishRef = useRef(false);
  const isOnFocusSiteRef = useRef(false);
  const { lang } = useParams();

  //   fetch risk data from api
  const fetchRiskData = useCallback(async () => {
    if (!currentRisk || currentTimeForecast === undefined) return;

    const calculateDateRange = (value) => {
      // ใช้ latestTimeForTheData จาก state ปัจจุบัน
      const currentLatestTime = latestTimeForTheData;
      if (!currentLatestTime) {
        return "";
      }

      // Parse the currentLatestTime into a Date object
      const latestDate = new Date(currentLatestTime);

      // Create new date by subtracting days based on value
      const targetDate = new Date(latestDate);
      targetDate.setDate(latestDate.getDate() - value);

      return moment(targetDate).format("YYYY-MM-DD");
    };

    const getDateRange = (value) => {
      if (value === 0) {
        return "";
      } else {
        return calculateDateRange(value);
      }
    };

    try {
      const riskValue = riskGroup.find(
        (risk) => risk.id === currentRisk
      )?.value;

      const payload = {
        hazard: riskValue,
        on_date: getDateRange(Number(currentTimeForecast)),
      };

      const response = await getRiskOnSiteMapDap(payload);

      if (response?.status) {
        setIsLoading(false);
        setRiskData(response.data);

        if (latestTimeForTheData === "") {
          setLatestTimeForTheData(response.data.date);
        }
      }
    } catch (error) {
      setRiskData([]);
      console.error("Error fetching risk data:", error);
    }
  }, [currentRisk, currentTimeForecast]); // ลบ latestTimeForTheData ออกจาก dependencies

  useEffect(() => {
    fetchRiskData();
  }, [fetchRiskData]);

  // useEffect แยกสำหรับการจัดการ latestTimeForTheData
  useEffect(() => {
    if (
      latestTimeForTheData &&
      currentRisk &&
      currentTimeForecast !== undefined
    ) {
      // เรียก fetchRiskData โดยตรงแทนการใช้ dependency
      const refetchData = async () => {
        if (!currentRisk || currentTimeForecast === undefined) return;

        const calculateDateRange = (value) => {
          const currentLatestTime = latestTimeForTheData;
          if (!currentLatestTime) {
            return "";
          }

          const latestDate = new Date(currentLatestTime);
          const targetDate = new Date(latestDate);
          targetDate.setDate(latestDate.getDate() - value);

          return moment(targetDate).format("YYYY-MM-DD");
        };

        const getDateRange = (value) => {
          if (value === 0) {
            return "";
          } else {
            return calculateDateRange(value);
          }
        };

        try {
          const riskValue = riskGroup.find(
            (risk) => risk.id === currentRisk
          )?.value;

          const payload = {
            hazard: riskValue,
            on_date: getDateRange(Number(currentTimeForecast)),
          };

          const response = await getRiskOnSiteMapDap(payload);

          if (response?.status) {
            setIsLoading(false);
            setRiskData(response.data);
          }
        } catch (error) {
          setRiskData([]);
          console.error("Error refetching risk data:", error);
        }
      };

      refetchData();
    }
  }, [latestTimeForTheData, currentRisk, currentTimeForecast]);

  // useEffect สำหรับแสดง risk data เมื่อ riskData เปลี่ยน
  useEffect(() => {
    // ตรวจสอบว่า riskData มี hazard array หรือไม่
    const hasValidRiskData =
      currentRisk &&
      riskData &&
      riskData.hazard &&
      Array.isArray(riskData.hazard);

    if (hasValidRiskData) {
      if (!map) return;

      // ลบ risk data เก่าถ้ามี (ป้องกันการซ้อนทับ)
      if (riskMarkersRef.current.length > 0) {
        riskMarkersRef.current.forEach((marker) => {
          if (map.hasLayer(marker)) {
            map.removeLayer(marker);
          }
        });
        riskMarkersRef.current = [];
      }

      if (riskCirclesRef.current.length > 0) {
        riskCirclesRef.current.forEach((circle) => {
          if (map.hasLayer(circle)) {
            map.removeLayer(circle);
          }
        });
        riskCirclesRef.current = [];
      }

      // สร้าง risk markers และ circles จาก risk data
      const newRiskMarkers = [];
      const newRiskCircles = [];

      //   TODO: เอา logic slice ออก เมื่อได้รับ ความต้องการชัดเจนแล้ว
      const dataToUse =
        riskData?.hazard &&
        Array.isArray(riskData.hazard) &&
        riskData.hazard.length > 0
          ? riskData.hazard.length > 500
            ? riskData.hazard.slice(0, 500)
            : riskData.hazard
          : [];

      if (dataToUse.length === 0) {
        // ลบ markers และ circles ที่มีอยู่แล้ว
        riskMarkersRef.current.forEach((marker) => {
          if (map.hasLayer(marker)) {
            map.removeLayer(marker);
          }
        });

        riskCirclesRef.current.forEach((circle) => {
          if (map.hasLayer(circle)) {
            map.removeLayer(circle);
          }
        });

        riskMarkersRef.current = [];
        riskCirclesRef.current = [];
        return;
      }

      dataToUse?.forEach((data, index) => {
        // สร้าง risk marker
        const riskMarker = L.marker([data.lat, data.lon], {
          icon: L.divIcon({
            html: getRiskIcon(currentRisk),
            className: "risk-marker",
            iconSize: [24, 24],
            iconAnchor: [12, 12],
          }),
        });

        // สร้าง risk circle
        const riskCircle = L.circle([data.lat, data.lon], {
          radius: data.radius_m,
          color: getRiskColor(currentRisk),
          fillColor: getRiskColor(currentRisk),
          fillOpacity: 0.2,
          weight: 2,
        });

        newRiskMarkers.push(riskMarker);
        newRiskCircles.push(riskCircle);
      });

      // เพิ่ม markers และ circles ลงในแผนที่ (ป้องกันการซ้อนทับ)
      newRiskMarkers.forEach((marker) => {
        if (!map.hasLayer(marker)) {
          map.addLayer(marker);
        }
      });
      newRiskCircles.forEach((circle) => {
        if (!map.hasLayer(circle)) {
          map.addLayer(circle);
        }
      });

      // อัปเดต ref
      riskMarkersRef.current = newRiskMarkers;
      riskCirclesRef.current = newRiskCircles;
    }
  }, [riskData, currentRisk]);

  // ฟังก์ชันสำหรับซ่อน risk data
  const onHideRiskData = useCallback(() => {
    if (!map) return;

    // ลบ risk markers
    riskMarkersRef.current.forEach((marker) => {
      if (map.hasLayer(marker)) {
        map.removeLayer(marker);
      }
    });

    // ลบ risk circles
    riskCirclesRef.current.forEach((circle) => {
      if (map.hasLayer(circle)) {
        map.removeLayer(circle);
      }
    });

    riskMarkersRef.current = [];
    riskCirclesRef.current = [];
  }, []); // ลบ dependencies ที่ทำให้เกิด infinite loop

  // Cleanup เมื่อ component unmount
  useEffect(() => {
    return () => {
      if (map) {
        riskMarkersRef.current.forEach((marker) => {
          if (map.hasLayer(marker)) {
            map.removeLayer(marker);
          }
        });
        riskCirclesRef.current.forEach((circle) => {
          if (map.hasLayer(circle)) {
            map.removeLayer(circle);
          }
        });
      }
    };
  }, []); // ไม่ต้องใส่ map เป็น dependency เพราะเป็น cleanup function

  useEffect(() => {
    const init = async () => {
      setId(randomString(10));
    };
    init();
  }, []);

  useEffect(() => {
    if (id) {
      onInitailizationMap();
    }
  }, [id]);

  useEffect(() => {
    if (Array.isArray(props.listSite) && gisMap) {
      onSetMarkerSite(props.listSite);
    }
  }, [props.listSite, gisMap]);

  useEffect(() => {
    if (props.focusSite) {
      onFocusSiteFromCard(props.focusSite);
    }
  }, [props.focusSite, listMarker]);

  useEffect(() => {
    if (gisMap) {
      if (currentRisk) {
      } else {
        onHideRiskData();
      }
    }
  }, [currentRisk, gisMap, onHideRiskData]);

  function randomString(length) {
    var text = "";
    var possible =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    for (var i = 0; i < length; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  //   switch map layer between street and satellite mode
  function toggleMapLayer() {
    if (!map || !currentTileLayerRef.current) return;

    // เก็บตำแหน่งและ zoom ปัจจุบัน
    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();

    // ลบ layer ปัจจุบัน
    map.removeLayer(currentTileLayerRef.current);

    // สลับ layer
    const newLayer = currentLayer === "satellite" ? "street" : "satellite";
    setCurrentLayer(newLayer);

    // สร้าง layer ใหม่
    let tileLayer = "https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}";
    let satelliteLayer = "https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}";

    let newTiles;
    if (newLayer === "satellite") {
      newTiles = L.tileLayer(satelliteLayer, {
        maxZoom: mapPosition.maxZoom,
        minZoom: mapPosition.minZoom,
        attribution: "© Google",
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        crossOrigin: true,
        noWrap: false, // อนุญาตให้แผนที่ wrap รอบโลก
        continuousWorld: true, // อนุญาตให้แผนที่ต่อเนื่องกัน
        wrapLatLng: true, // อนุญาตให้ wrap latitude/longitude
        errorTileUrl:
          "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
        // เพิ่มการตั้งค่าสำหรับ smooth loading
        keepBuffer: 2, // เก็บ tiles เพิ่มเติมใน buffer
        updateWhenZooming: false, // ไม่ update tiles เมื่อ zoom
        updateWhenIdle: true, // update tiles เมื่อ idle
        zIndex: 1,
      });
    } else {
      newTiles = L.tileLayer(tileLayer, {
        maxZoom: mapPosition.maxZoom,
        minZoom: mapPosition.minZoom,
        attribution: "© Google",
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        crossOrigin: true,
        noWrap: false, // อนุญาตให้แผนที่ wrap รอบโลก
        continuousWorld: true, // อนุญาตให้แผนที่ต่อเนื่องกัน
        wrapLatLng: true, // อนุญาตให้ wrap latitude/longitude
        errorTileUrl:
          "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
        // เพิ่มการตั้งค่าสำหรับ smooth loading
        keepBuffer: 2, // เก็บ tiles เพิ่มเติมใน buffer
        updateWhenZooming: false, // ไม่ update tiles เมื่อ zoom
        updateWhenIdle: true, // update tiles เมื่อ idle
        zIndex: 1,
      });
    }

    // เพิ่ม layer ใหม่
    newTiles.addTo(map);
    currentTileLayerRef.current = newTiles;

    // คืนตำแหน่งและ zoom เดิม
    map.setView(currentCenter, currentZoom);
  }

  function createPropertyData(data) {
    const getnameMarker = data?.Name
      ? data?.Name.includes('"name_english"')
        ? JSON.parse(data?.Name)
        : data?.Name
      : "";

    const nameMarker =
      typeof getnameMarker === "string"
        ? getnameMarker
        : lang === "th"
        ? getnameMarker?.name_thai
        : getnameMarker?.name_english;

    let location = {};
    try {
      if (data?.location && typeof data.location === "string") {
        location = JSON.parse(data.location);
      }
    } catch (error) {
      location = {};
    }

    return {
      name: nameMarker || "Property Asset",
      assetCode: data?.basic_info?.asset_code || "N/A",
      propertyType: data?.basic_info?.property_type || "N/A",
      location: `${location?.state}, ${location?.country}` || "N/A",
      status: data?.operational_status?.property_status || "N/A",
    };
  }

  async function onInitailizationMap() {
    try {
      if (!gisMap) {
        map = L.map(`map${id}`, {
          center: [mapPosition.latitude, mapPosition.longitude],
          zoom: mapPosition.zoom,
          zoomSnap: mapPosition.zoomSnap,
          renderer: L.canvas({
            padding: 0.5,
            tolerance: 3,
          }),
          worldCopyJump: false, // ปิดการ jump เพื่อป้องกัน empty map
          maxBoundsViscosity: 0.0, // อนุญาตให้เลื่อนข้ามขอบเขตได้ (สำหรับ world wrap)
          // เพิ่มการตั้งค่าสำหรับการข้ามขอบเขต
          crs: L.CRS.EPSG3857, // ใช้ Web Mercator projection
          zoomAnimation: true,
          fadeAnimation: true,
          markerZoomAnimation: true,
          // เพิ่มการตั้งค่าสำหรับ smooth panning
          preferCanvas: true, // ใช้ Canvas renderer สำหรับ performance ที่ดีกว่า
          zoomControl: true,
          attributionControl: true,
          // ปิดการใช้งาน features ที่ไม่จำเป็นเพื่อเพิ่ม performance
          doubleClickZoom: true,
          dragging: true,
          keyboard: true,
          scrollWheelZoom: true,
          touchZoom: true,
          boxZoom: true,
        });
        setGisMap(map);
      } else {
        map = gisMap;
      }
      //==============================
      let tileLayer = "https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}";
      let satelliteLayer = "https://{s}.google.com/vt/lyrs=y&x={x}&y={y}&z={z}";

      // สร้าง tile layers
      const streetTiles = L.tileLayer(tileLayer, {
        maxZoom: mapPosition.maxZoom,
        minZoom: mapPosition.minZoom,
        attribution: "© Google",
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        crossOrigin: true,
        noWrap: false, // อนุญาตให้แผนที่ wrap รอบโลก
        continuousWorld: true, // อนุญาตให้แผนที่ต่อเนื่องกัน
        wrapLatLng: true, // อนุญาตให้ wrap latitude/longitude
        errorTileUrl:
          "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
        // เพิ่มการตั้งค่าสำหรับ smooth loading
        keepBuffer: 2, // เก็บ tiles เพิ่มเติมใน buffer
        updateWhenZooming: false, // ไม่ update tiles เมื่อ zoom
        updateWhenIdle: true, // update tiles เมื่อ idle
        zIndex: 1,
      });

      const satelliteTiles = L.tileLayer(satelliteLayer, {
        maxZoom: mapPosition.maxZoom,
        minZoom: mapPosition.minZoom,
        attribution: "© Google",
        subdomains: ["mt0", "mt1", "mt2", "mt3"],
        crossOrigin: true,
        noWrap: false, // อนุญาตให้แผนที่ wrap รอบโลก
        continuousWorld: true, // อนุญาตให้แผนที่ต่อเนื่องกัน
        wrapLatLng: true, // อนุญาตให้ wrap latitude/longitude
        errorTileUrl:
          "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==",
        // เพิ่มการตั้งค่าสำหรับ smooth loading
        keepBuffer: 2, // เก็บ tiles เพิ่มเติมใน buffer
        updateWhenZooming: false, // ไม่ update tiles เมื่อ zoom
        updateWhenIdle: true, // update tiles เมื่อ idle
        zIndex: 1,
      });

      // เพิ่ม layer เริ่มต้น
      const initialTiles =
        currentLayer === "satellite" ? satelliteTiles : streetTiles;
      initialTiles.addTo(map);
      currentTileLayerRef.current = initialTiles;

      // ซ่อน grid overlay และเพิ่ม smooth rendering
      setTimeout(() => {
        const mapContainer = document.getElementById(`map${id}`);
        if (mapContainer) {
          const style = document.createElement("style");
          style.textContent = `
            .leaflet-tile-pane .leaflet-tile {
              image-rendering: -webkit-optimize-contrast;
              image-rendering: crisp-edges;
            }
            .leaflet-tile-pane .leaflet-tile-container {
              opacity: 1 !important;
            }
            /* เพิ่ม smooth rendering สำหรับการเลื่อน */
            .leaflet-container {
              -webkit-transform: translateZ(0);
              transform: translateZ(0);
              -webkit-backface-visibility: hidden;
              backface-visibility: hidden;
              -webkit-perspective: 1000;
              perspective: 1000;
            }
            .leaflet-tile-pane {
              -webkit-transform: translateZ(0);
              transform: translateZ(0);
            }
            .leaflet-marker-pane {
              -webkit-transform: translateZ(0);
              transform: translateZ(0);
            }
            /* เพิ่ม smooth transition สำหรับ markers */
            .leaflet-marker-icon {
              transition: transform 0.1s ease-out;
            }
            /* เพิ่ม smooth rendering สำหรับการข้ามขอบเขต */
            .leaflet-tile-pane {
              will-change: transform;
              -webkit-transform: translate3d(0, 0, 0);
              transform: translate3d(0, 0, 0);
            }
            .leaflet-map-pane {
              will-change: transform;
            }
            /* ป้องกันการ flicker เมื่อข้ามขอบเขต */
            .leaflet-container {
              -webkit-transform: translate3d(0, 0, 0);
              transform: translate3d(0, 0, 0);
            }
            /* เพิ่มการตั้งค่าสำหรับการแสดงผลที่ต่อเนื่อง */
            .leaflet-tile {
              image-rendering: -webkit-optimize-contrast;
              image-rendering: crisp-edges;
              image-rendering: pixelated;
            }
            /* ป้องกันการแสดงผลที่ขาดหาย */
            .leaflet-tile-pane {
              opacity: 1 !important;
            }
          `;
          mapContainer.appendChild(style);
        }
      }, 100);

      //==============================
      map.zoomControl.setPosition("bottomright");
      //==============================
      await onSetMapEvent();
      //==============================
    } catch (e) {}
  }

  function onSetMapEvent() {
    if (!map) return;

    // เพิ่ม event สำหรับการข้ามขอบเขต (ใช้ flag เพื่อป้องกัน infinite loop)
    let isAdjustingView = false;
    map.on("moveend", () => {
      if (isAdjustingView) return; // ป้องกัน infinite loop

      const center = map.getCenter();
      if (center.lng > 180) {
        isAdjustingView = true;
        map.setView([center.lat, center.lng - 360], map.getZoom(), {
          animate: false,
        });
        setTimeout(() => {
          isAdjustingView = false;
        }, 100);
      } else if (center.lng < -180) {
        isAdjustingView = true;
        map.setView([center.lat, center.lng + 360], map.getZoom(), {
          animate: false,
        });
        setTimeout(() => {
          isAdjustingView = false;
        }, 100);
      }
    });

    map.on("zoomend", (event) => {
      const target = event.target;
      const latlng = target.getCenter();
      const zoom = target.getZoom();

      try {
        props.onChangeCenterMap({
          Latitude: latlng.lat,
          Longitude: latlng.lng,
          Zoom: zoom,
        });
      } catch {}

      if (!isOnFocusSiteRef.current) {
        onGetLatLngFromContainer();
      }
      isInitFirstFinishRef.current = true;
    });

    map.on("dragend", (event) => {
      const target = event.target;
      const latlng = target.getCenter();
      const zoom = target.getZoom();

      try {
        props.onChangeCenterMap({
          Latitude: latlng.lat,
          Longitude: latlng.lng,
          Zoom: zoom,
        });
      } catch {}

      if (!isOnFocusSiteRef.current) {
        onGetLatLngFromContainer();
      }
    });
  }
  //==============================
  async function onSetMarkerSite(param) {
    if (map && markerCluster) map.removeLayer(markerCluster);
    //================================
    let listX = [];
    let listMarkerX = [];
    let listMarkerXWithId = [];
    listSiteRef.current = [];
    try {
      listX = JSON.parse(JSON.stringify(param));
    } catch {}

    for (let item of listX) {
      listSiteRef.current.push(item);
      if (item.Active) {
        const marker = await onCreateMarkerSite(item);
        if (marker) {
          listMarkerX.push(marker);
          listMarkerXWithId.push({
            Id: item.Id,
            Latitude: item.Latitude,
            Longitude: item.Longitude,
            Marker: marker,
            data: item, // เพิ่มข้อมูลของ item
          });
        }
      }
    }
    setListMarker(listMarkerXWithId);
    //================================
    let markerClusterX = L.markerClusterGroup({
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      zoomToBoundsOnClick: true,
      disableClusteringAtZoom: mapPosition.maxZoom,
      iconCreateFunction: (e) => {
        let styleCluster = {
          bgColor: "#DC5D5E",
          fontColor: "#FFFFFF",
          fontSize: "10px",
          fontWeight: 600,
          boxShadow: "0px 16px 16px rgba(0, 0, 0, 0.16)",
        };
        return L.divIcon({
          html: `<div 
                    style="
                    display: flex;align-items: center;justify-content: center;position: relative;
                    ">
                    <div
                    style="
                    display: flex;align-items: center;justify-content: center;position: absolute;
                    box-shadow: ${styleCluster.boxShadow};
                    background-color: ${styleCluster.bgColor};
                    color: ${styleCluster.fontColor};
                    font-size: ${styleCluster.fontSize};
                    font-weight: ${styleCluster.fontWeight};
                    border-radius: 50%;
                    width: 28px;
                    height: 28px;
                    ">
                    <span style="padding-bottom: 2px;">${e.getChildCount()}</span>
                    </div>
                    </div>`,
          className: "",
        });
      },
    });
    markerClusterX.addLayers(listMarkerX);
    if (map) {
      map.addLayer(markerClusterX);
      setMarkerCluster(markerClusterX);
      //================================
      if (isInitFirstFinishRef.current) return;
      let listLatLngBound = [];
      for (let item of listSiteRef.current) {
        if (item.Latitude && item.Longitude && item.Active) {
          let detail = [item.Latitude, item.Longitude];
          listLatLngBound.push(detail);
        }
      }
      if (listLatLngBound.length > 0) {
        map.fitBounds(listLatLngBound);
        const zoomBound = map.getZoom();
        map.setZoom(zoomBound - 1);
      }
    }
  }
  async function onCreateMarkerSite(param) {
    if (!map) return;
    if (!param.Latitude || !param.Longitude) return;

    let marker = L.marker([param.Latitude, param.Longitude]);
    //==============================
    let icon;
    let svgPin = "";
    svgPin =
      '<div style="display: flex;align-items: center;justify-content: center;position: relative;">' +
      `<img style='width: 24px;position: absolute;' src='${imgPin}' />` +
      "</div>";
    icon = L.divIcon({
      html: svgPin,
      className: "",
      iconAnchor: [0, 0],
    });
    marker.options.icon = icon;
    marker.options.alt = JSON.stringify(param);
    //==============================
    //=============event============
    marker.on("click", (event) => {
      const element = event.target;
      let obj;
      try {
        obj = JSON.parse(element.options.alt);
      } catch {}

      if (obj) {
        const propertyData = createPropertyData(obj);
        const popupHTML = createPropertyPopupHTMLInline(propertyData, lang);

        element.unbindPopup();

        element
          .bindPopup(popupHTML, {
            offset: L.point(0, -10),
            closeButton: true,
            autoClose: false,
          })
          .openPopup();

        // Call onMarkerClick callback to focus the corresponding item in the list
        if (props.onMarkerClick && obj.Id) {
          props.onMarkerClick(obj.Id);
        }
      }
    });

    // marker.on("mouseover", (event) => {
    //   const element = event.target;
    //   let obj;
    //   try {
    //     obj = JSON.parse(element.options.alt);
    //   } catch {}
    //   onSetPopupMarker(true, element, obj, 7, 0);
    // });

    // marker.on("mouseout", (event) => {
    //   const element = event.target;
    //   let obj;
    //   try {
    //     obj = JSON.parse(element.options.alt);
    //   } catch {}
    //   onSetPopupMarker(false, element, obj, 7, 0);
    // });
    //==============================
    return marker;
  }

  //   async function onSetPopupMarker(showPopup, marker, data, x, y) {

  //     const getnameMarker = data?.Name
  //       ? data?.Name.includes('"nameEnglish"')
  //         ? JSON.parse(data?.Name)
  //         : data?.Name
  //       : "";

  //     const nameMarker =
  //       typeof getnameMarker === "string"
  //         ? getnameMarker
  //         : lang === "th"
  //         ? getnameMarker?.nameThai
  //         : getnameMarker?.nameEnglish;

  //     if (marker) {
  //       if (showPopup) {
  //         // สร้างข้อมูล property สำหรับ popup
  //         const propertyData = {
  //           name: nameMarker,
  //           assetCode: data?.AssetCode || "N/A",
  //           propertyType: data?.PropertyType || "N/A",
  //           location: data?.Location || "N/A",
  //           status: data?.Status || "Active",
  //         };

  //         const popupHTML = createPropertyPopupHTMLInline(propertyData, lang);

  //         marker
  //           .bindPopup(popupHTML, { offset: L.point(x, y), closeButton: false })
  //           .openPopup();
  //       } else {
  //         marker.closePopup();
  //       }
  //     }
  //   }

  //==============================
  function onGetLatLngFromContainer() {
    if (!divMapRef.current || !map) return;

    const posTopLeft = L.point([
      divMapRef.current.offsetLeft,
      divMapRef.current.offsetTop,
    ]);
    const posBottomRight = L.point([
      divMapRef.current.offsetLeft + divMapRef.current.offsetWidth,
      divMapRef.current.offsetTop + divMapRef.current.offsetHeight,
    ]);
    latLngTopLeftRef.current = map.containerPointToLatLng(posTopLeft);
    latLngBottomRightRef.current = map.containerPointToLatLng(posBottomRight);
    //==============================
    if (isInitFirstFinishRef.current) {
      let listX = [];
      for (let item of listSiteRef.current) {
        const check = onCheckMarkerInContainer(item);
        const detail = {
          ...item,
          Visible: check,
        };
        listX.push(detail);
      }
      try {
        props.onChangeVisibleSite(listX);
      } catch {}
    }
  }
  function onCheckMarkerInContainer(param) {
    let inside = false;
    let x = param.Latitude,
      y = param.Longitude;

    if (
      latLngTopLeftRef.current.lat >= x &&
      latLngBottomRightRef.current.lat <= x
    ) {
      if (
        latLngTopLeftRef.current.lng <= y &&
        latLngBottomRightRef.current.lng >= y
      ) {
        inside = true;
      }
    }

    return inside;
  }
  function onFocusSiteFromCard(param) {
    isOnFocusSiteRef.current = true;
    //==============================
    let model = listMarker.find((x) => x.Id === param.Id);
    if (model && map) {
      if (model.Latitude && model.Longitude) {
        const zoom = mapPosition.maxZoom;
        const center = [model.Latitude, model.Longitude];
        map.setView(center, zoom);

        // แสดง popup ค้างไว้เมื่อ focus จาก card
        setTimeout(() => {
          const marker = model.Marker;
          const data = model.data;
          if (marker && data) {
            const propertyData = createPropertyData(data);
            const popupHTML = createPropertyPopupHTMLInline(propertyData, lang);

            marker.unbindPopup();

            if (markerCluster) {
              const markersAtLocation = listMarker.filter(
                (m) =>
                  m.Latitude === model.Latitude &&
                  m.Longitude === model.Longitude
              );

              if (markersAtLocation.length > 1) {
                map.setView(
                  [model.Latitude, model.Longitude],
                  mapPosition.maxZoom
                );

                setTimeout(() => {
                  const cluster = markerCluster.getVisibleParent(marker);
                  if (cluster && cluster !== marker) {
                    const currentZoom = map.getZoom();
                    if (currentZoom < mapPosition.maxZoom) {
                      map.setZoom(mapPosition.maxZoom);
                      setTimeout(() => {
                        cluster.fire("click");
                        setTimeout(() => {
                          marker
                            .bindPopup(popupHTML, {
                              offset: L.point(0, -10),
                              closeButton: true,
                              autoClose: false,
                            })
                            .openPopup();
                        }, 300);
                      }, 200);
                    } else {
                      cluster
                        .bindPopup(popupHTML, {
                          offset: L.point(0, -10),
                          closeButton: true,
                          autoClose: false,
                        })
                        .openPopup();
                    }
                  } else {
                    marker
                      .bindPopup(popupHTML, {
                        offset: L.point(0, -10),
                        closeButton: true,
                        autoClose: false,
                      })
                      .openPopup();
                  }
                }, 300);
                return;
              }
            }

            marker
              .bindPopup(popupHTML, {
                offset: L.point(0, -10),
                closeButton: true,
                autoClose: false,
              })
              .openPopup();
          }
        }, 300);
      }
    }
    //==============================
    setTimeout(() => {
      isOnFocusSiteRef.current = false;
    }, 1000);
  }

  return (
    <div className="relative w-full h-full">
      <div
        ref={divMapRef}
        id={`map${id}`}
        className="z-20 w-full h-full"
        style={{
          "--leaflet-grid-opacity": "0",
        }}
      ></div>

      {/* btn switch map layer  */}
      <button
        onClick={toggleMapLayer}
        className="absolute top-4 right-4 z-30 bg-white hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg shadow-lg border border-gray-200 flex items-center gap-2 transition-colors duration-200"
        title={currentLayer === "satellite" ? "Street Map" : "Satellite Map"}
      >
        <svg
          className="w-4 h-4 text-vekin"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {currentLayer === "satellite" ? (
            // Street Map Icon
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
            />
          ) : (
            // Satellite Icon
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          )}
        </svg>
      </button>

      {/* btn risk icon */}
      {/* <div className="absolute top-4 left-4 z-30">
        <BtnRiskIcon
          currentRisk={currentRisk}
          setIsLoading={setIsLoading}
          onSetCurrentRisk={(e) => {
            setRiskData([]);
            setCurrentTimeForecast(0);
            setCurrentRisk(e);
          }}
        />
      </div> */}

      {/* card risk details  :: TODO : open after the data has a risk value*/}
      {/* {currentRisk && (
        <div className="absolute top-16 right-4 z-30">
          <CardRiskDetails currentRisk={currentRisk} />
        </div>
      )} */}

      {/* card time forecast */}
      {currentRisk && (
        <div className="absolute bottom-3 right-14 z-30">
          <CardTimeForeCast
            currentTimeForecast={currentTimeForecast}
            setCurrentTimeForecast={(e) => {
              setIsLoading(true);
              setCurrentTimeForecast(e);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default DapGisMapComponent;
